import { useState } from 'react';
import { useAuditLog } from './cpc-hooks';
import { cpcApi } from './cpc-api';
import type { AuditEntityType, AuditLogFilter } from './cpc-types';

const PAGE_SIZE = 50;

const entityTypeLabels: Record<AuditEntityType, string> = {
  role_rate: 'Tagessatz',
  factor_set: 'Faktor-Set',
  calculation: 'Kalkulation',
  clockodo: 'Clockodo',
};

const actionLabels: Record<string, string> = {
  create: 'Anlegen',
  update: 'Bearbeiten',
  delete: 'Löschen',
  activate: 'Aktivieren',
  sync: 'Synchronisieren',
  link: 'Verknüpfen',
  create_project: 'Projekt anlegen',
};

const inputStyle: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: '0.875rem',
};

const btnStyle = (variant: 'primary' | 'secondary' = 'secondary'): React.CSSProperties => ({
  padding: '0.35rem 0.85rem',
  border: '1px solid ' + (variant === 'primary' ? 'var(--color-primary)' : 'var(--color-border)'),
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.8rem',
  background: variant === 'primary' ? 'var(--color-primary)' : 'transparent',
  color: variant === 'primary' ? '#fff' : 'var(--color-text)',
});

export function AuditLogTab() {
  const [entityType, setEntityType] = useState<AuditEntityType | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filter: AuditLogFilter = {
    entityType: entityType || undefined,
    from: from || undefined,
    to: to || undefined,
    search: search || undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading, isError } = useAuditLog(filter);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const handleReset = () => {
    setEntityType('');
    setFrom('');
    setTo('');
    setSearch('');
    setPage(1);
  };

  const handleExport = () => {
    const exportFilter: AuditLogFilter = {
      entityType: entityType || undefined,
      from: from || undefined,
      to: to || undefined,
      search: search || undefined,
    };
    const url = cpcApi.getAuditLogExportUrl(exportFilter);
    window.open(url, '_blank');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Audit-Log</h3>
        <button style={btnStyle('secondary')} onClick={handleExport}>CSV exportieren</button>
      </div>

      {/* Filter-Leiste */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <select
          style={{ ...inputStyle, minWidth: 140 }}
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value as AuditEntityType | ''); setPage(1); }}
        >
          <option value="">Alle Entitäten</option>
          {(Object.keys(entityTypeLabels) as AuditEntityType[]).map((k) => (
            <option key={k} value={k}>{entityTypeLabels[k]}</option>
          ))}
        </select>

        <input
          style={{ ...inputStyle, width: 140 }}
          type="date"
          value={from}
          onChange={(e) => { setFrom(e.target.value); setPage(1); }}
          title="Von"
          placeholder="Von"
        />
        <input
          style={{ ...inputStyle, width: 140 }}
          type="date"
          value={to}
          onChange={(e) => { setTo(e.target.value); setPage(1); }}
          title="Bis"
          placeholder="Bis"
        />

        <input
          style={{ ...inputStyle, minWidth: 180 }}
          placeholder="Suche (ID, Aktion…)"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />

        <button style={btnStyle()} onClick={handleReset}>Zurücksetzen</button>
      </div>

      {/* Status */}
      {data && (
        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0 0 0.5rem' }}>
          {data.total} Einträge gesamt
        </p>
      )}

      {/* Tabelle */}
      {isLoading ? (
        <p style={{ fontSize: '0.875rem' }}>Lädt…</p>
      ) : isError ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--color-danger, #dc3545)' }}>Fehler beim Laden des Audit-Logs.</p>
      ) : !data || data.events.length === 0 ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>Keine Einträge gefunden.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left', background: 'var(--color-bg-alt, #f8f9fa)' }}>
                <th style={{ padding: '0.4rem 0.6rem' }}>Datum/Zeit</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>User-ID</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>Entität</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>ID</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>Aktion</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((ev) => (
                <tr key={ev.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.35rem 0.6rem', whiteSpace: 'nowrap' }}>
                    {new Date(ev.createdAt).toLocaleString('de-DE')}
                  </td>
                  <td style={{ padding: '0.35rem 0.6rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {ev.userId.slice(0, 8)}…
                  </td>
                  <td style={{ padding: '0.35rem 0.6rem' }}>
                    <span style={{
                      padding: '0.1rem 0.4rem',
                      borderRadius: '3px',
                      background: 'var(--color-bg-alt, #f0f0f0)',
                      fontSize: '0.75rem',
                    }}>
                      {entityTypeLabels[ev.entityType] ?? ev.entityType}
                    </span>
                  </td>
                  <td style={{ padding: '0.35rem 0.6rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {ev.entityId.length > 12 ? `${ev.entityId.slice(0, 8)}…` : ev.entityId}
                  </td>
                  <td style={{ padding: '0.35rem 0.6rem' }}>
                    <ActionBadge action={ev.action} />
                  </td>
                  <td style={{ padding: '0.35rem 0.6rem', color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                    {ev.detail ? (
                      <code style={{ wordBreak: 'break-all' }}>
                        {Object.entries(ev.detail)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(' · ')}
                      </code>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginierung */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            style={btnStyle()}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            &laquo; Zurück
          </button>
          <span style={{ fontSize: '0.8rem' }}>Seite {page} / {totalPages}</span>
          <button
            style={btnStyle()}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Weiter &raquo;
          </button>
        </div>
      )}
    </div>
  );
}

function ActionBadge({ action }: { action: string }) {
  const colors: Record<string, string> = {
    create: '#d1fae5',
    update: '#dbeafe',
    delete: '#fee2e2',
    activate: '#fef9c3',
    sync: '#e0e7ff',
    link: '#e0e7ff',
    create_project: '#d1fae5',
  };
  const textColors: Record<string, string> = {
    create: '#065f46',
    update: '#1e40af',
    delete: '#991b1b',
    activate: '#713f12',
    sync: '#3730a3',
    link: '#3730a3',
    create_project: '#065f46',
  };
  return (
    <span style={{
      padding: '0.1rem 0.4rem',
      borderRadius: '3px',
      fontSize: '0.75rem',
      background: colors[action] ?? '#f3f4f6',
      color: textColors[action] ?? '#374151',
    }}>
      {actionLabels[action] ?? action}
    </span>
  );
}
